import { useState, useMemo } from "react";
import cx from "classnames";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import InputFile from "components/elements/inputFile";
import ImageBox from "components/elements/imageBox";
import ModalEditLinkCert from "./modalEditLinkCert";

const defaultSds =
    "https://cdn.cotik.app/1756445828413_DARK_GRAY_ENAMEL_MODEL_CODE_25841_PART_NO__87541087_CAT__NO__ATPDG_0002.pdf";
const MAX_CERTIFICATION_FILES = 4;

const ProductAttribute = ({
    attributes,
    formData,
    generateAiAttr,
    setFormData,
    certifications = [],
    handleChangeCertifications,
    showBaseCertifications = true,
}) => {
    const [openProductAttrs, setOpenProductAttr] = useState(true);
    const [openProductCom, setOpenProductCom] = useState(true);
    const { attributeCondition, attributeBase, attributeCompliance } = useMemo(() => {
        return attributes?.reduce(
            ({ attributeCondition, attributeBase, attributeCompliance }, attr) => {
                if (attr?.requirement_conditions?.length) {
                    const keyObj = attr?.requirement_conditions?.map((req) => req.attribute_id)[0];
                    attributeCondition[keyObj] = attributeCondition[keyObj]
                        ? [...attributeCondition[keyObj], attr]
                        : [attr];
                } else {
                    if (!(!attr.is_customizable && !attr.is_multiple_selection && !attr.is_requried)) {
                        attributeBase.push(attr);
                    } else {
                        attributeCompliance.push(attr);
                    }
                }
                return { attributeCondition, attributeBase, attributeCompliance };
            },
            { attributeCondition: {}, attributeBase: [], attributeCompliance: [] },
        );
    }, [attributes]);
    const certificationsCondition = useMemo(() => {
        return (
            certifications?.reduce((result, cert, key) => {
                if (!cert?.requirement_conditions?.length) return result;
                const keyObj = cert?.requirement_conditions?.[0]?.attribute_id;
                const certData = { ...cert, baseKey: key };
                result[keyObj] = result[keyObj] ? [...result[keyObj], certData] : [certData];
                return result;
            }, {}) || {}
        );
    }, [certifications]);
    const listCertifications = useMemo(() => {
        return (
            certifications
                ?.filter((cert) => !cert?.requirement_conditions?.length && cert?.is_required === true)
                .map((cert, key) => ({ ...cert, baseKey: key })) || []
        );
    }, [certifications]);

    const { t } = useTranslation();
    // if (!Boolean(attributes?.length)) return;

    const handleChangeProductAttrs = (data, attrId) => {
        if (!Array.isArray(data)) data = [data];
        const values = data.reduce((result, item) => {
            if (item) {
                let val = { id: item?.value, name: item?.label };
                if (item?.["__isNew__"] || !Number.isInteger(+item?.value)) {
                    val = { name: item.label };
                }
                result.push(val);
            }

            return result;
        }, []);

        setFormData((prev) => {
            const prevAttributes = prev?.product_attributes || [];
            let result = [...prevAttributes, { id: attrId, values }];
            const exist = prevAttributes?.find((a) => a.id === attrId);
            const arrClear = attributeCondition[attrId];
            const certClear = certificationsCondition[attrId];
            if (exist) {
                result = prevAttributes.map((a) => {
                    if (a.id === attrId) return { id: attrId, values };
                    return a;
                });
            }
            const clearDataAttribute = (data, clear) => {
                const _arrClear = [];
                const _attrs = data.reduce((result, attr) => {
                    if (!clear.find((ac) => attr.id === ac.id)) {
                        result.push(attr);
                    } else if (attributeCondition[attr.id]) {
                        _arrClear.push(...attributeCondition[attr.id]);
                    }
                    return result;
                }, []);
                if (_arrClear.length) {
                    return clearDataAttribute(_attrs, _arrClear);
                }
                return _attrs;
            };
            if (arrClear?.length) {
                result = clearDataAttribute(result, arrClear);
            }

            const nextCertifications =
                certClear?.length && prev?.certifications?.length
                    ? prev.certifications.filter((cert) => !certClear.some((element) => element.id === cert?.id))
                    : prev?.certifications;

            return { ...prev, product_attributes: result, certifications: nextCertifications };
        });
    };

    const getCertUrls = (cert) => {
        if (Array.isArray(cert?.urls)) return cert.urls.filter(Boolean).slice(0, MAX_CERTIFICATION_FILES);
        if (Array.isArray(cert?.url)) return cert.url.filter(Boolean).slice(0, MAX_CERTIFICATION_FILES);
        if (Array.isArray(cert?.value)) return cert.value.filter(Boolean).slice(0, MAX_CERTIFICATION_FILES);
        const url = cert?.url || cert?.value || "";
        return url ? [url] : [];
    };

    const getFormCertification = (certId) => {
        return formData?.certifications?.find((cert) => cert?.id === certId);
    };

    const updateCertificationUrls = (c, urls) => {
        setFormData((prev) => {
            const value = [...(prev?.certifications || [])].filter((cert) => cert?.id !== c.id);
            const nextUrls = Array.from(new Set((Array.isArray(urls) ? urls : [urls]).filter(Boolean))).slice(
                0,
                MAX_CERTIFICATION_FILES,
            );
            value.push({ id: c.id, url: nextUrls });
            return {
                ...prev,
                certifications: value.filter((cert) => getCertUrls(cert).length),
            };
        });
    };

    const removeCertificationUrl = (c, url) => {
        setFormData((prev) => {
            const certifications = (prev?.certifications || []).reduce((result, cert) => {
                if (cert?.id !== c.id) {
                    result.push(cert);
                    return result;
                }

                const urls = getCertUrls(cert).filter((certUrl) => certUrl !== url);
                if (urls.length) result.push({ id: c.id, url: urls });
                return result;
            }, []);
            return {
                ...prev,
                certifications,
            };
        });
    };

    const drawCert = (c, key) => {
        const certUrls = getCertUrls(getFormCertification(c.id));
        return (
            <div className="mt-4 fv-row fv-plugins-icon-container" key={key}>
                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                    <span className={c?.is_required ? "required" : ""}>{c?.name}</span>
                </label>
                <InputFile
                    accept="image/*,application/pdf"
                    onChange={(e) => handleChangeCertifications(c.id, e, c.baseKey)}
                >
                    <button
                        className="btn btn-danger"
                        data-bs-toggle="modal"
                        data-bs-target="#kt_modal_create_app"
                        id="kt_toolbar_primary_button"
                    >
                        <i className="ki-outline ki-cloud-download"></i>
                        {t("Upload Certifications")}
                    </button>
                </InputFile>
                <ModalEditLinkCert
                    onSubmit={(data) => {
                        updateCertificationUrls(c, data);
                    }}
                />
                <button
                    className="btn btn-info ms-4"
                    onClick={() => {
                        updateCertificationUrls(c, defaultSds);
                    }}
                >
                    {t("Using File Default")}
                </button>
                <div className="mt-4">
                    {!!certUrls?.length && certUrls.map((url) => drawPreview(url, c))}
                </div>
            </div>
        );
    };

    const drawPreview = (url, c) => {
        const extension = url.split(".").pop().split("?")[0].toLowerCase();
        const previewKey = `${c.baseKey}-${url}`;
        if (extension === "pdf") {
            return (
                <div
                    key={previewKey}
                    className="al-thumb position-relative preview-pdf"
                    onClick={() => window.open(url, "_blank")}
                >
                    File pdf
                    <button
                        type="button"
                        className="btn-delete-image"
                        onClick={(e) => {
                            e.stopPropagation();
                            removeCertificationUrl(c, url);
                        }}
                    >
                        <i className="fa fa-trash" />
                    </button>
                </div>
            );
        }
        return (
            <ImageBox
                key={previewKey}
                src={url}
                onRemove={() => {
                    removeCertificationUrl(c, url);
                }}
            />
        );
    };

    const drawFormAttr = (attr) => {
        return (
            <>
                {attr.is_customizable ? (
                    <CreatableSelect
                        isClearable
                        isMulti={attr.is_multiple_selection}
                        onChange={(data) => handleChangeProductAttrs(data, attr.id)}
                        value={
                            formData?.product_attributes
                                ?.find((a) => a.id === attr.id)
                                ?.values.map((v, i) => {
                                    return { label: v.name, value: v.id || v.name };
                                }) || []
                        }
                        className="w-100"
                        options={attr?.values.map((value) => ({
                            label: `${value?.name}`,
                            value: value?.id,
                        }))}
                        name="options-variants-value"
                        styles={{ height: "100%" }}
                        placeholder={t(`Select or Create a ${attr.name} ... `)}
                    />
                ) : (
                    <Select
                        isClearable
                        isMulti={attr.is_multiple_selection}
                        name="shop"
                        options={attr?.values.map((value) => ({
                            label: `${value?.name}`,
                            value: value?.id,
                        }))}
                        value={
                            formData?.product_attributes
                                ?.find((a) => a.id === attr.id)
                                ?.values.map((v, i) => {
                                    return { label: v.name, value: v.id || v.name };
                                }) || []
                        }
                        className="basic-multi-select al-custom-select-search-variants mt-1 me-2 mb-0 al-filter-table w-100"
                        classNamePrefix="select"
                        onChange={(data) => handleChangeProductAttrs(data, attr.id)}
                        placeholder={t(`Select a ${attr.name} ... `)}
                    />
                )}

                {Boolean(attributeCondition[attr.id]) &&
                    attributeCondition[attr.id].map((attr, key) => {
                        const condition = attr.requirement_conditions?.some((c) => {
                            return formData?.product_attributes
                                ?.find((v) => v.id === c.attribute_id)
                                ?.values?.find((value) => value.id === c.attribute_value_id);
                        });

                        if (!condition) return null;
                        return (
                            <div key={key} className="w-100 mt-2">
                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                    <span className={attr.is_requried ? "required" : ""}>{attr.name}</span>
                                </label>
                                {drawFormAttr(attr)}
                            </div>
                        );
                    })}

                {Boolean(certificationsCondition[attr.id]) &&
                    certificationsCondition[attr.id]?.map((cert, key) => {
                        const condition = cert.requirement_conditions?.some((c) => {
                            return formData?.product_attributes
                                ?.find((v) => v.id === c.attribute_id)
                                ?.values?.find((value) => value.id === c.attribute_value_id);
                        });

                        if (!condition) return null;
                        return drawCert(cert, key);
                    })}
            </>
        );
    };

    return (
        <>
            {showBaseCertifications && Boolean(listCertifications?.length) && (
                <div className="d-flex flex-column mb-4 fv-row fv-plugins-icon-container">
                    <label className="d-flex align-items-center fs-6 fw-semibold mb-2 gap-2">
                        <h3 className="mb-0">{t("Certifications")}</h3>
                    </label>
                    <div className="row">{listCertifications?.map((c, key) => drawCert(c, key))}</div>
                </div>
            )}
            {Boolean(attributes?.length) && (
                <>
                    <div className="d-flex flex-column mb-4 fv-row fv-plugins-icon-container">
                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2 gap-2">
                            <h3 className="mb-0">{t("Product Attributes")}</h3>
                            <div onClick={() => setOpenProductAttr(!openProductAttrs)} className={"rotate-180"}>
                                <i
                                    className={`ki-duotone ki-${openProductAttrs ? "up" : "down"} fs-1 cursor-pointer`}
                                ></i>
                            </div>
                        </label>
                        <div className="my-4">
                            <button className="btn btn-info" onClick={generateAiAttr}>
                                Generate AI
                            </button>
                        </div>
                        {openProductAttrs && (
                            <div className="row">
                                {attributeBase
                                    .sort(
                                        (a, b) =>
                                            Boolean(attributeCondition[a.id] || certificationsCondition[a.id]) -
                                            Boolean(attributeCondition[b.id] || certificationsCondition[b.id]),
                                    )
                                    .map((attr, key) => (
                                        <div
                                            key={key}
                                            className={cx(
                                                Boolean(attributeCondition[attr.id] || certificationsCondition[attr.id])
                                                    ? `col-12 my-4`
                                                    : `col-3 mb-2`,
                                            )}
                                        >
                                            <div
                                                className={cx({
                                                    "border shadow-sm p-4": Boolean(
                                                        attributeCondition[attr.id] || certificationsCondition[attr.id],
                                                    ),
                                                })}
                                            >
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span className={attr.is_requried ? "required" : ""}>
                                                        {attr.name}
                                                    </span>
                                                </label>
                                                {drawFormAttr(attr)}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                    <div className="d-flex flex-column mb-4 fv-row fv-plugins-icon-container">
                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2 gap-2">
                            <h3 className="mb-0">{t("Product Compliance")}</h3>
                            <div onClick={() => setOpenProductCom(!openProductCom)} className={"rotate-180"}>
                                <i
                                    className={`ki-duotone ki-${openProductCom ? "up" : "down"} fs-1 cursor-pointer`}
                                ></i>
                            </div>
                        </label>
                        {openProductCom && (
                            <div className="row">
                                {attributeCompliance.map((attr, key) => (
                                    <div className="col-12 mb-2" key={key}>
                                        <div
                                            key={key}
                                            className={cx({
                                                "border shadow-sm p-4 my-4": Boolean(
                                                    attributeCondition[attr.id] || certificationsCondition[attr.id],
                                                ),
                                            })}
                                        >
                                            <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                <span className={attr.is_requried ? "required" : ""}>{attr.name}</span>
                                            </label>
                                            {drawFormAttr(attr)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default ProductAttribute;
